import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { EmptyState } from '@/lib/base/empty-state';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { GroupedList, type GroupedListSection } from '@/lib/base/grouped-list';
import { QueryErrorState } from '@/lib/components';
import type { AiActionsView } from '@/lib/localStorage/aiActionsSortPrefs';

import type { ProposalListItem } from '../../lib/proposals/core/types';
import { useMyResolvedProposalsInfinite } from '../../lib/proposals/hooks/useProposals';
import { proposalDetailQuery } from '../../lib/proposals/query/queries';
import { ActionTableRow } from './ActionTableRow';
import RecipientAvatar from './RecipientAvatar';
import { ActionListSkeleton } from './shared/ActionListSkeleton';
import { groupProposalsByTier } from './utils/groupProposalsByTier';

type ResolutionKind = 'approved' | 'rejected' | 'withdrawn';

interface ProposalHistoryListProps {
  orgId: number;
  resolution: ResolutionKind;
  /** Called with the first row's id when the first page loads. */
  onFirstItemLoaded?: (id: number) => void;
}

const HISTORY_PAGE_SIZE = 50;
const WINDOW_CAPTION = 'Showing the last 7 days';

/** The view tab each history resolution belongs to — passed as a navigation
    state hint so the tab doesn't flash while the action detail loads. */
const VIEW_FOR_RESOLUTION: Record<ResolutionKind, AiActionsView> = {
  approved: 'done',
  rejected: 'dismissed',
  withdrawn: 'archived',
};

const EMPTY_STATE_COPY: Record<
  ResolutionKind,
  { heading: string; body: string }
> = {
  approved: {
    heading: 'No approved actions',
    body: 'Nothing was approved in the last 7 days.',
  },
  rejected: {
    heading: 'No dismissed actions',
    body: 'Nothing was dismissed in the last 7 days.',
  },
  withdrawn: {
    heading: 'No archived actions',
    body: 'Nothing was archived in the last 7 days.',
  },
};

export function ProposalHistoryList({
  orgId,
  resolution,
  onFirstItemLoaded,
}: ProposalHistoryListProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data,
    isLoading,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useMyResolvedProposalsInfinite(orgId, resolution, HISTORY_PAGE_SIZE, {
    enabled: orgId > 0,
  });

  const handleRowClick = useCallback(
    (id: number) => {
      navigate(`/${orgId}/actions/${id}`, {
        state: { view: VIEW_FOR_RESOLUTION[resolution] },
      });
    },
    [navigate, orgId, resolution]
  );

  const getSourceIcon = useCallback(
    (action: ProposalListItem) => (
      <RecipientAvatar action={action} size='xxs' />
    ),
    []
  );

  const results = useMemo(
    () => data?.pages.flatMap(p => p.results) ?? [],
    [data]
  );
  const copy = EMPTY_STATE_COPY[resolution];

  // Report the first loaded row so the parent can auto-select it when
  // nothing is selected (mirrors the Open list's auto-select behavior).
  // Appending pages never changes the first row's id, so this fires once
  // per resolution rather than once per loaded page.
  const firstItemId = results.length > 0 ? results[0].id : null;
  useEffect(() => {
    if (firstItemId != null) {
      onFirstItemLoaded?.(firstItemId);
    }
  }, [firstItemId, onFirstItemLoaded]);

  // Infinite load: fetch the next page when the sentinel below the last row
  // scrolls into view. Re-arming on results.length matters — observe() always
  // reports the initial intersection, so if a short page leaves the sentinel
  // still visible the next page loads without another scroll event.
  // fetchNextPage while a fetch is in flight is a no-op, so no extra guard.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage) {
      return;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        fetchNextPage();
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, fetchNextPage, results.length]);

  // ── GroupedList wiring — mirrors the Open list so history views render
  // the same tier groupings (High / Medium / CRM updates) and the same
  // single-line row treatment. ─────────────────────────────────────────
  const listSections = useMemo<GroupedListSection<ProposalListItem>[]>(
    () =>
      groupProposalsByTier(results).map(group => ({
        id: group.tier,
        label: group.label,
        items: group.actions,
      })),
    [results]
  );

  const getRowId = useCallback(
    (action: ProposalListItem) => String(action.id),
    []
  );
  const getRowLabel = useCallback(
    (action: ProposalListItem) => action.title,
    []
  );
  const renderRow = useCallback(
    (action: ProposalListItem) => (
      <ActionTableRow
        action={action}
        icon={getSourceIcon(action)}
        isNew={false}
        isHistory
      />
    ),
    [getSourceIcon]
  );
  const handleItemClick = useCallback(
    (action: ProposalListItem) => handleRowClick(action.id),
    [handleRowClick]
  );

  // Prefetch details for rows the virtualizer materializes — replaces the
  // old per-row mousedown prefetch (same pattern as the Open list). The ref
  // dedupes across repeated onVisibleItemsChange invocations.
  const prefetchedIdsRef = useRef<Set<number>>(new Set());
  const handleVisibleItemsChange = useCallback(
    (visible: ProposalListItem[]) => {
      for (const action of visible) {
        if (!prefetchedIdsRef.current.has(action.id)) {
          prefetchedIdsRef.current.add(action.id);
          queryClient.prefetchQuery(proposalDetailQuery(orgId, action.id));
        }
      }
    },
    [queryClient, orgId]
  );

  // End-of-list chrome renders in GroupedList's footer slot so it scrolls
  // with the rows: the infinite-load sentinel (zero-height; the loader row
  // is the visible cue), the loading indicator, and the 7-day window
  // caption (only meaningful once the user reaches the end of the list).
  const listFooter = useMemo(
    () => (
      <>
        {hasNextPage && <div ref={sentinelRef} aria-hidden='true' />}
        {isFetchingNextPage && (
          <div
            data-testid='history-loading-more'
            className='flex justify-center border-t border-[var(--color-border-secondary)] py-3'
          >
            <LoadingIndicator type='line-spinner' size='sm' />
          </div>
        )}
        {!hasNextPage && (
          <p className='px-4 pt-3 pb-4 text-center text-xs text-fg-tertiary'>
            {WINDOW_CAPTION}
          </p>
        )}
      </>
    ),
    [hasNextPage, isFetchingNextPage]
  );

  return (
    <div className='flex flex-col h-full'>
      {/* Initial load renders the same row skeleton as the Open list, so
          switching tabs never flashes a different loading treatment. */}
      {isLoading && !data && <ActionListSkeleton />}

      {error && (
        <div className='flex flex-1 items-center justify-center'>
          <QueryErrorState error={error} label="We couldn't load history" />
        </div>
      )}

      {!isLoading && !error && data && results.length === 0 && (
        <div className='flex flex-1 items-center justify-center'>
          <EmptyState title={copy.heading} description={copy.body} />
        </div>
      )}

      {!error && results.length > 0 && (
        <GroupedList
          aria-label='Resolved actions'
          sections={listSections}
          getItemId={getRowId}
          getItemLabel={getRowLabel}
          renderItem={renderRow}
          onItemClick={handleItemClick}
          onVisibleItemsChange={handleVisibleItemsChange}
          footer={listFooter}
        />
      )}
    </div>
  );
}
